import sleep from 'lib/sleep';

export const DEFAULT_TIMEOUT_MS = 60 * 1000; // 60 seconds
export const DEFAULT_SLEEP_MS = 1 * 1000; // 1 second

type ErrorWithCode = Error & { code?: string };

type FunctionResolveType<T> = T extends (...args: any[]) => Promise<infer X> ? X : never;

type PromiseFunction = (...args: any) => Promise<any>;

export interface PollOptions<T = PromiseFunction> {
  requestFn: T;
  isFinished: (result: FunctionResolveType<T>) => Promise<boolean> | boolean;
  sleepMs?: number;
  timeoutMs?: number;
}

const errorWithCode = ({ code, message }: { code?: string; message: string }) => {
  const error = new Error(message) as ErrorWithCode;
  error.code = code;
  return error;
};

export async function poll<T extends PromiseFunction>({
  requestFn,
  isFinished,
  sleepMs = DEFAULT_SLEEP_MS,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: PollOptions<T>) {
  let response: FunctionResolveType<T>;
  let stopPolling = false;
  const startTime = Date.now();

  do {
    const lastRequestTime = Date.now();
    response = await requestFn();
    stopPolling = await isFinished(response);

    if (stopPolling) {
      break;
    }

    if (Date.now() - startTime + sleepMs > timeoutMs) {
      throw errorWithCode({
        code: 'POLL_TIMEOUT',
        message: `Desired state not reached after ${timeoutMs}ms`,
      });
    }

    const requestDuration = Date.now() - lastRequestTime;
    const sleepForMs = Math.max(0, sleepMs - requestDuration);

    await sleep(sleepForMs);
  } while (!stopPolling);

  return response;
}
